import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useTheme } from '../styles/use-theme.js';
import { radius, spacing, typography } from '../styles/theme.js';
import { PinIcon, FavoriteIcon, FavoriteOutlineIcon } from './icons.js';
import { NoteVisibilityBadge } from './note-visibility-badge.js';
/** 从 Markdown 内容提取纯文本摘要 */
function extractSummary(content, maxLen = 60) {
    if (!content)
        return '';
    const text = content
        .replace(/```[\s\S]*?```/g, ' ')
        .replace(/`[^`]*`/g, ' ')
        .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
        .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
        .replace(/[#>*_~-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    if (text.length <= maxLen)
        return text;
    return `${text.slice(0, maxLen)}...`;
}
/** 格式化时间（相对/绝对） */
function formatTime(ts) {
    const diff = Date.now() - ts;
    if (diff < 60000)
        return '刚刚';
    if (diff < 3600000)
        return `${Math.floor(diff / 60000)} 分钟前`;
    if (diff < 86400000)
        return `${Math.floor(diff / 3600000)} 小时前`;
    const d = new Date(ts);
    const now = new Date();
    if (d.getFullYear() === now.getFullYear()) {
        return `${d.getMonth() + 1}/${d.getDate()}`;
    }
    return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
}
export function NoteListItem({ note, notebookName, onOpen, onToggleFavorite }) {
    const { theme } = useTheme();
    const containerStyle = {
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.sm,
        padding: spacing.lg,
        backgroundColor: theme.bg.secondary,
        borderRadius: radius.lg,
        border: `1px solid ${theme.border.default}`,
        cursor: onOpen ? 'pointer' : 'default',
        transition: 'border-color 0.12s ease, box-shadow 0.12s ease, transform 0.12s ease',
    };
    const handleMouseEnter = (e) => {
        e.currentTarget.style.borderColor = theme.border.hover;
        e.currentTarget.style.boxShadow = theme.shadow.sm;
        e.currentTarget.style.transform = 'translateY(-1px)';
    };
    const handleMouseLeave = (e) => {
        e.currentTarget.style.borderColor = theme.border.default;
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
    };
    const titleNode = (_jsxs("div", { style: { display: 'flex', alignItems: 'flex-start', gap: spacing.sm }, children: [_jsx("span", { style: {
                    flex: 1,
                    fontSize: typography.fontSize.lg,
                    fontWeight: typography.fontWeight.semibold,
                    color: theme.text.primary,
                    lineHeight: 1.4,
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                }, children: note.title || '无标题' }), _jsxs("span", { style: { display: 'inline-flex', gap: spacing.xs, alignItems: 'center', flexShrink: 0 }, children: [note.isPinned && (_jsx("span", { style: { display: 'inline-flex', color: theme.status.warning }, title: "\u5DF2\u7F6E\u9876", children: _jsx(PinIcon, {}) })), _jsx("button", { onClick: (e) => {
                            e.stopPropagation();
                            onToggleFavorite?.(note);
                        }, style: {
                            display: 'inline-flex',
                            padding: 0,
                            border: 'none',
                            background: 'transparent',
                            cursor: onToggleFavorite ? 'pointer' : 'default',
                            color: note.isFavorite ? '#f59e0b' : theme.text.tertiary,
                        }, title: note.isFavorite ? '取消收藏' : '收藏', children: note.isFavorite ? _jsx(FavoriteIcon, {}) : _jsx(FavoriteOutlineIcon, {}) })] })] }));
    const summary = extractSummary(note.summary || note.content);
    const summaryNode = summary ? (_jsx("p", { style: {
            margin: 0,
            fontSize: typography.fontSize.base,
            color: theme.text.secondary,
            lineHeight: typography.lineHeight.relaxed,
        }, children: summary })) : null;
    const tagNodes = (note.tags ?? []).map(tag => (_jsxs("span", { style: {
            padding: '2px 8px',
            borderRadius: radius.full,
            backgroundColor: theme.interaction.hover,
            color: theme.text.secondary,
            fontSize: typography.fontSize.xs,
        }, children: ["#", tag] }, tag)));
    const metaNode = (_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: spacing.md, flexWrap: 'wrap' }, children: [_jsx("span", { style: { fontSize: typography.fontSize.xs, color: theme.text.tertiary }, children: notebookName ? `📔 ${notebookName}` : '' }), _jsx("span", { style: { fontSize: typography.fontSize.xs, color: theme.text.tertiary }, children: formatTime(note._updatedAt ?? note._createdAt) }), _jsx(NoteVisibilityBadge, { visibility: note.visibility, isArchived: note.isArchived }), _jsx("div", { style: { display: 'flex', gap: spacing.xs, flex: 1, justifyContent: 'flex-end' }, children: tagNodes })] }));
    return (_jsxs("div", { style: containerStyle, onClick: () => onOpen?.(note), onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, children: [titleNode, summaryNode, metaNode] }));
}
//# sourceMappingURL=note-list-item.js.map